
import React from "react";
import { Menu, MenuItem, Typography } from "@mui/material";

export default function ShareMenu({
  anchorEl,
  open,
  onClose,
  shareUrl,
  shareType = "notice", // "notice" | "vanity"
  title,
  talentName,
  pay,
  location,
  deadline,
  projectType,
  unionStatus,
  onCopySuccess,
}) {
  const isVanityShare = shareType === "vanity";

  const brandName = "DirectSubmit";
  const brandDescription =
    "Connect Talent with Casting Directors";

  const profileName =
    talentName?.trim() || "Talent Profile";

  const vanityTitle =
    `${brandName} | ${brandDescription} | ${profileName}`;

  const vanityDescription = talentName
    ? `Explore ${profileName}'s talent profile on DirectSubmit. Connect with talent and casting directors.`
    : "Explore my talent profile on DirectSubmit. Connect with talent and casting directors.";

  const formatDate = (date) => {
    if (!date) return "";

    const parsedDate = new Date(date);

    return Number.isNaN(parsedDate.getTime())
      ? ""
      : parsedDate.toLocaleDateString();
  };

  const handleCopy = async () => {
    if (!shareUrl) return;

    try {
      await navigator.clipboard.writeText(shareUrl);
      onCopySuccess?.();
      onClose();
    } catch (error) {
      console.error("Failed to copy share link:", error);
    }
  };

  // Talent profile message used by WhatsApp and other text-based sharing.
  const vanityMessage = [
    vanityTitle,
    "",
    vanityDescription,
    "",
    "View the talent profile:",
    shareUrl,
    "",
    "Powered by DirectSubmit",
  ].join("\n");

  // Existing casting notice message.
  const noticeMessage = [
    "🎬 CASTING CALL",
    "",
    title && `🎭 Project: ${title}`,
    projectType && `🎥 Type: ${projectType}`,
    location && `📍 Location: ${location}`,
    unionStatus && `🤝 Union: ${unionStatus}`,
    pay && `💰 Compensation: ${pay}`,
    deadline && `📅 Submission Deadline: ${formatDate(deadline)}`,
    "",
    "View the complete casting call and apply here:",
    shareUrl,
    "",
    "Powered by DirectSubmit",
  ]
    .filter(Boolean)
    .join("\n");

  const shareMessage = isVanityShare
    ? vanityMessage
    : noticeMessage;

  const encodedMessage = encodeURIComponent(shareMessage);
  const encodedUrl = encodeURIComponent(shareUrl || "");

  const emailSubject = isVanityShare
    ? vanityTitle
    : `Casting Call${title ? ` - ${title}` : ""}`;

  const emailBody = isVanityShare
    ? [
        `Hello,`,
        "",
        vanityDescription,
        "",
        `View ${profileName}'s profile here:`,
        shareUrl,
        "",
        "Powered by DirectSubmit",
      ].join("\n")
    : noticeMessage;

  const encodedEmailSubject = encodeURIComponent(emailSubject);
  const encodedEmailBody = encodeURIComponent(emailBody);

  const urls = {
    WHATSAPP: isVanityShare
      ? `https://api.whatsapp.com/send?text=${encodedMessage}`
      : `https://api.whatsapp.com/send?text=${encodedMessage}`,

    EMAIL:
      `mailto:?subject=${encodedEmailSubject}&body=${encodedEmailBody}`,

    TWITTER:
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        isVanityShare ? vanityTitle : shareMessage
      )}&url=${encodedUrl}`,

    LINKEDIN: isVanityShare
      ? `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`
      : `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,

    FACEBOOK:
      `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
  };

  const handleShare = (platform) => {
    if (!shareUrl || !urls[platform]) return;

    if (platform === "EMAIL") {
      window.location.href = urls.EMAIL;
    } else {
      window.open(
        urls[platform],
        "_blank",
        "noopener,noreferrer"
      );
    }

    onClose();
  };

  const menuItemStyle = {
    justifyContent: "center",
    backgroundColor: "#8fb2ff",
    color: "#fff",
    borderRadius: 1,
    fontSize: "0.75rem",
    fontWeight: 600,
    mb: 0.75,
    mx: 1,
    "&:hover": {
      backgroundColor: "#739cff",
    },
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      disableScrollLock
      PaperProps={{
        sx: {
          borderRadius: 2,
          p: 1.5,
          minWidth: 200,
          boxShadow: "0px 6px 18px rgba(0, 0, 0, 0.15)",
        },
      }}
      MenuListProps={{
        sx: { p: 0 },
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 600,
          mb: 1,
          px: 1,
        }}
      >
        Share via
      </Typography>

      <MenuItem
        onClick={handleCopy}
        sx={menuItemStyle}
        disabled={!shareUrl}
      >
        COPY LINK
      </MenuItem>

      {[
        "WHATSAPP",
        "EMAIL",
        "TWITTER",
        "LINKEDIN",
        "FACEBOOK",
      ].map((platform) => (
        <MenuItem
          key={platform}
          onClick={() => handleShare(platform)}
          sx={menuItemStyle}
          disabled={!shareUrl}
        >
          {platform}
        </MenuItem>
      ))}
    </Menu>
  );
}
